/* jshint esversion: 6 */
/* jshint esversion: 8*/
/* jshint esversion: 10 */
/* jshint ignore:start */

import { useCallback, useContext, useEffect, useState } from 'react';
import {
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
  useRemoteUsers,
} from 'agora-rtc-react';
import { FiMicOff, FiMic, FiPhoneCall } from 'react-icons/fi';
import { IoCall } from 'react-icons/io5';
import { MyContext } from '../Context/MyProvider';
import { fetchAgoraToken } from '../utils/agoraToken';
import { getSocket } from '../socket';
import { showTost } from '../showTost';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

const Basics = ({ Id, receiverId, name, img, channelname, credentials }) => {
  const { setIsVoiceCalling, setAtendCall, setCallstatus, agoraAppId, basUrl } = useContext(MyContext);

  const [calling, setCalling] = useState(false);
  const isConnected = useIsConnected();
  const [channel, setChannel] = useState(channelname);
  const [token, setToken] = useState(null);
  const [agoraUid, setAgoraUid] = useState(null);
  const [rtcAppId, setRtcAppId] = useState(agoraAppId);
  const [micOn, setMic] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [callError, setCallError] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const peerId = receiverId || Id;

  const { localMicrophoneTrack, error: microphoneError } = useLocalMicrophoneTrack(micOn);
  usePublish(localMicrophoneTrack ? [localMicrophoneTrack] : [], isConnected);

  const remoteUsers = useRemoteUsers();
  const { audioTracks: remoteAudioTracks, error: remoteAudioError } = useRemoteAudioTracks(remoteUsers);
  const hasRemoteAudio = remoteAudioTracks.length > 0 || remoteUsers.length > 0;

  const closeLocalTracks = useCallback(() => {
    if (localMicrophoneTrack) {
      try {
        localMicrophoneTrack.stop();
        localMicrophoneTrack.close();
      } catch (e) {
        console.error("Error closing mic track:", e);
      }
    }
  }, [localMicrophoneTrack]);

  const joinReady = calling && Boolean(rtcAppId || agoraAppId) && Boolean(channel) && Number(agoraUid) > 0;
  const { error: joinError } = useJoin(
    { appid: rtcAppId || agoraAppId, channel, token: token || null, uid: Number(agoraUid) },
    joinReady
  );

  useEffect(() => {
    remoteAudioTracks.forEach((track) => {
      try {
        track.play();
      } catch (error) {
        console.error("Unable to play remote audio track:", error);
      }
    });

    return () => {
      remoteAudioTracks.forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.error("Unable to stop remote audio track:", error);
        }
      });
    };
  }, [remoteAudioTracks]);
  
  useEffect(() => {
    return () => {
      closeLocalTracks();
    };
  }, [closeLocalTracks]);

  useEffect(() => {
    if (!credentials) return;
    setCallError("");
    setRtcAppId(credentials.agora_app_id || credentials.appId || agoraAppId);
    setToken(credentials.token || null);
    setAgoraUid(Number(credentials.uid || 0));
    setChannel(credentials.channel || channelname);
    setCalling(true);
  }, [credentials, channelname, agoraAppId]);

  useEffect(() => {
    if (credentials) return undefined;
    if (!name || !channelname) return;
    let cancelled = false;
    setCallError("");
    fetchAgoraToken(basUrl, channelname, "audio", peerId)
      .then((data) => {
        if (cancelled) return;
        setRtcAppId(data.appId || agoraAppId);
        setToken(data.token || null);
        setAgoraUid(data.uid || null);
        setChannel(data.channel || channelname);
        setCalling(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        const message = error?.message || "Unable to start audio call.";
        setCallError(message);
        showTost({ title: message });
        setCalling(false);
      });
    return () => {
      cancelled = true;
    };
  }, [peerId, name, channelname, basUrl, agoraAppId, credentials]);

  useEffect(() => {
    if (!microphoneError) return;
    const message = "Microphone permission is blocked. Allow microphone access in the browser and try again.";
    setCallError(message);
    showTost({ title: message });
  }, [microphoneError]);

  useEffect(() => {
    if (!joinError) return;
    if (joinError?.code === "INVALID_OPERATION" || joinError?.message?.includes("INVALID_OPERATION")) return;
    console.error(joinError);
    const message = joinError?.message || "Unable to join audio call.";
    setCallError(message);
    showTost({ title: message });
  }, [joinError]);

  useEffect(() => {
    if (!remoteAudioError) return;
    console.error(remoteAudioError);
    const message = "Unable to connect remote audio. Please retry the call.";
    setCallError(message);
    showTost({ title: message });
  }, [remoteAudioError]);

  useEffect(() => {
    if (!accepted || (!hasRemoteAudio && !isConnected)) {
      setElapsedSeconds(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setElapsedSeconds((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [accepted, hasRemoteAudio, isConnected]);

  const closeCallUi = useCallback(() => {
    closeLocalTracks();
    setIsVoiceCalling(false);
    setCalling(false);
    setCallstatus(false);
    setAtendCall(false);
  }, [closeLocalTracks, setIsVoiceCalling, setCallstatus, setAtendCall]);

  const leaveCall = useCallback(async () => {
    const socket = getSocket();
    if (socket?.connected && peerId && channel) {
      socket.emit("call_end", {
        otherId: peerId,
        channel,
        status: "completed",
        reason: "ended_by_user",
      });
    }
    closeCallUi();
  }, [peerId, channel, closeCallUi]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const handleCallAccepted = (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      setAccepted(true);
    };

    const handleCallEnded = (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      closeCallUi();
    };

    socket.on("call_accepted", handleCallAccepted);
    socket.on("call_ended", handleCallEnded);
    socket.on("call_rejected", handleCallEnded);
    return () => {
      socket.off("call_accepted", handleCallAccepted);
      socket.off("call_ended", handleCallEnded);
      socket.off("call_rejected", handleCallEnded);
    };
  }, [channel, closeCallUi]);

  const statusText = callError
    ? callError
    : hasRemoteAudio
      ? "Connected"
      : accepted || isConnected
        ? "Connecting..."
        : "Ringing...";

  return (
    <div className="relative h-[min(660px,90vh)] min-h-[540px] w-full overflow-hidden rounded-[30px] bg-[#190028] text-white shadow-2xl">
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-[16px]"
        style={{ backgroundImage: `url(${img})` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(171,34,255,0.95)_0%,rgba(117,21,208,0.96)_42%,rgba(37,6,58,0.98)_100%)]" />
      <div className="absolute left-1/2 top-24 h-56 w-56 -translate-x-1/2 rounded-full bg-white/12 blur-3xl" />
      <div className="absolute -left-16 bottom-20 h-56 w-56 rounded-full bg-[#ff4f8b]/20 blur-3xl" />
      <div className="relative z-[2] flex h-full flex-col items-center justify-between px-6 py-8">
        <div className="rounded-full bg-white/14 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiPhoneCall />
            Audio call
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-[-18px] animate-pulse rounded-full bg-white/18 blur-md" />
            <div className="absolute inset-[-34px] rounded-full border border-white/10" />
            <img
              src={img}
              alt={name || "Caller"}
              className="relative h-36 w-36 rounded-full border-[5px] border-white object-cover shadow-2xl"
            />
          </div>
          <h2 className="m-0 max-w-full px-4 text-4xl font-bold leading-tight">{name}</h2>
          <p className="m-0 mt-2 text-sm font-medium text-white/75">{statusText}</p>
          <p className="m-0 mt-4 rounded-full bg-black/22 px-5 py-2 text-sm font-semibold tracking-wide text-white/90 shadow-inner">
            {formatDuration(elapsedSeconds)}
          </p>
        </div>

        <div className="flex w-full max-w-[340px] items-center justify-between rounded-[30px] bg-black/42 px-9 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          <button
            type="button"
            onClick={() => setMic((prev) => !prev)}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`group flex h-16 w-16 items-center justify-center rounded-full transition duration-200 hover:-translate-y-0.5 hover:shadow-xl ${micOn ? "bg-black/70 text-white" : "bg-white text-[#190028]"}`}
          >
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button
            type="button"
            onClick={leaveCall}
            title="End call"
            aria-label="End call"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]"
          >
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Basics;
/* jshint ignore:end */
